<script setup lang="ts">
import { ElMessage } from "element-plus";
import { useAudioVideoCapture } from "~/composable/useAudioVideoCapture";
import { useSupportStore } from "~/stores/supportStore";
import { useAuthStore } from "~/stores/auth";
import { useStatusStore } from "~/stores/statusStore";
import { play as playSound } from "~/projects/cc/services/AudioService";
import { splitIntoLinkSegments } from "~/utils/linkHelper";

type TicketFormAttachment = {
  type: "file" | "screenshot" | "video";
  data: File;
  preview?: string;
};

type ViewMode = "list" | "create";

const MAX_ATTACHMENTS_COUNT = 10;
const MAX_SINGLE_ATTACHMENT_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENTS_SIZE_BYTES = 50 * 1024 * 1024;
const TOO_MANY_ATTACHMENTS = `A message can carry at most ${MAX_ATTACHMENTS_COUNT} attachments`;

/*
 * How far under the per-file limit a screen recording stops itself.
 *
 * The recorder is measured on what it has handed over so far, and its last
 * second arrives after it has been told to stop, so it is cut a megabyte early
 * rather than on the line. At the recorder's bitrate that is about ten seconds
 * of footage, which is cheaper than an upload the server turns away.
 */
const RECORDING_HEADROOM_BYTES = 1024 * 1024;
/*
 * Below this much room left on the message, a recording is not offered. It
 * would stop itself after a few seconds, which is not worth a screen picker.
 */
const RECORDING_MIN_BYTES = 2 * 1024 * 1024;
/*
 * How long the panel takes to get out of the shot. TransWrap animates it out
 * over 0.2s, and a capture that starts any sooner has the panel fading out in
 * the frame. A little over, for the paint after the animation ends.
 */
const PANEL_LEAVE_MS = 250;

const ALLOWED_ATTACHMENT_MIME_PREFIXES = ["image/", "video/", "audio/"];
const ALLOWED_ATTACHMENT_MIME_TYPES = [
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip",
  "application/x-zip-compressed",
];

const formContainer = ref<HTMLElement | null>(null);
const showSupportPopup = ref(false);
const viewMode = ref<ViewMode>("list");
const supportStore = useSupportStore();
const statusStore = useStatusStore();

const ticketForm = ref({
  title: "",
  problemStatement: "",
  attachments: [] as TicketFormAttachment[],
});

const previewAttachment = ref<TicketFormAttachment | null>(null);
const chatBoxMounted = ref(true);
const chatBoxRenderKey = ref(0);
const currentPage = ref(1);
const ticketOriginUrl = ref("");

const {
  recordingState,
  screenshotingState,
  lastVideo,
  takeScreenshot,
  startRecording,
  stopRecording,
  stopSharing,
  clearLastScreenshot,
  clearLastVideo,
  dispose,
} = useAudioVideoCapture();

const tickets = computed(() => supportStore.tickets);
const isCreatingTicket = computed(() => supportStore.isCreatingTicket);
const unread = computed(() => supportStore.unread);

/* ------------------------------------------------------------------------ *
 * Dragging the bubble
 *
 * Every fixed position for this button is wrong on some screen. Bottom-left
 * covers the inbox's quick-action chips; bottom-right covers the send button;
 * anywhere in between covers the conversation. There is no default that works
 * for everyone, so the answer is not a better default — it is letting people
 * put it where their own screen has room, and remembering that.
 *
 * Deliberately NOT edge-snapping the way Meet's picture-in-picture does.
 * Snapping is there to keep a video tile clear of a full-bleed page. Here the
 * things it collides with — the chip strip, the composer row — run edge to
 * edge, so the only positions that actually work are the small gaps between
 * controls, and snapping would take away precisely those.
 * ------------------------------------------------------------------------ */

/** Never closer than this to any edge, dragged or clamped. */
const EDGE_GUTTER = 12;
/** A press has to travel this far before it is a drag rather than a tap. */
const DRAG_THRESHOLD = 6;
/** Between the bubble and the panel it opens. */
const POPUP_GAP = 12;
/** Fallback bubble size for the frame before anything has been measured. */
const BUBBLE_FALLBACK = 48;

const authStore = useAuthStore();

const launcherButton = ref<HTMLElement | null>(null);
const supportPopup = ref<{ $el?: HTMLElement } | null>(null);

/** Viewport top-left of the bubble, or null while it is wherever CSS put it. */
const placement = ref<{ left: number; top: number } | null>(null);
const bubbleDragging = ref(false);

/*
 * Per user, not per browser. Agents share machines here and signing out does
 * not clear localStorage, so the id belongs in the KEY: two agents on one
 * browser keep two positions, and it is the key changing that puts the bubble
 * back where the current agent left it rather than where the last one did.
 */
const placementKey = computed(
  () => `support.launcher.position.${authStore.user?.id ?? "anon"}`,
);

/*
 * The bubble is not allowed over the app's own header.
 *
 * Free placement is the point of the drag, but the header is the one region
 * where "wherever you like" is wrong: it is sticky, it is where the call,
 * status, notification and menu controls live, and a bubble parked on it covers
 * them on every page at once rather than on the one screen the agent happened
 * to be looking at when they put it there.
 *
 * Measured off `.sticky-nav` rather than read from `--topbar-height`, so this
 * does not quietly depend on the layout publishing that variable; capped at two
 * fifths of the viewport so a tall header on a small phone cannot fence off
 * most of the screen.
 */
const topBound = () => {
  const bar = document.querySelector<HTMLElement>(".sticky-nav");
  const below = bar ? bar.getBoundingClientRect().bottom + EDGE_GUTTER : 0;
  return Math.min(Math.max(EDGE_GUTTER, below), window.innerHeight * 0.4);
};

const bubbleSize = () => {
  const rect = launcherButton.value?.getBoundingClientRect();
  return {
    width: rect?.width || BUBBLE_FALLBACK,
    height: rect?.height || BUBBLE_FALLBACK,
  };
};

/** Put the bubble at (left, top), pulled inside the viewport if that is outside it. */
const placeBubble = (left: number, top: number) => {
  const { width, height } = bubbleSize();
  const minTop = topBound();
  const maxLeft = Math.max(
    EDGE_GUTTER,
    window.innerWidth - width - EDGE_GUTTER,
  );
  const maxTop = Math.max(minTop, window.innerHeight - height - EDGE_GUTTER);

  placement.value = {
    left: Math.min(Math.max(EDGE_GUTTER, left), maxLeft),
    top: Math.min(Math.max(minTop, top), maxTop),
  };
};

const savePlacement = () => {
  if (!placement.value) return;
  try {
    localStorage.setItem(placementKey.value, JSON.stringify(placement.value));
  } catch {
    // Storage blocked or full. The bubble still moves for this visit; it just
    // will not be where they left it next time, which is not worth an error.
  }
};

const restorePlacement = () => {
  try {
    const raw = localStorage.getItem(placementKey.value);
    if (!raw) return;
    const saved = JSON.parse(raw);
    if (typeof saved?.left !== "number" || typeof saved?.top !== "number")
      return;

    /*
     * A stored position over the header is discarded, not clamped.
     *
     * Clamping would slide the bubble to just under the header — a spot nobody
     * picked, on every page, and still in the way. Falling back to the default
     * corner is the honest reading of "that position is not allowed any more",
     * and it means one saved before this rule existed heals itself on the next
     * load instead of having to be dragged off.
     */
    if (saved.top < topBound()) {
      localStorage.removeItem(placementKey.value);
      return;
    }

    // Otherwise through the clamp, so a position saved on a desktop does not
    // open off the side of a phone.
    placeBubble(saved.left, saved.top);
  } catch {
    // Unreadable or not ours. The default position is a fine place to start.
  }
};

let pointerId: number | null = null;
let grabX = 0;
let grabY = 0;
let grabLeft = 0;
let grabTop = 0;
/*
 * Whether this press turned into a drag. Read by the click handler, because a
 * pointerup that ends a drag is followed by a click, and that click is the drag
 * finishing rather than somebody asking for the panel.
 */
let dragMoved = false;

const endDragListeners = () => {
  window.removeEventListener("pointermove", onLauncherPointerMove);
  window.removeEventListener("pointerup", onLauncherPointerUp);
  window.removeEventListener("pointercancel", onLauncherPointerUp);
};

const onLauncherPointerDown = (event: PointerEvent) => {
  if (event.button !== 0) return;
  const button = launcherButton.value;
  if (!button) return;

  const rect = button.getBoundingClientRect();
  grabLeft = rect.left;
  grabTop = rect.top;
  grabX = event.clientX;
  grabY = event.clientY;
  dragMoved = false;
  pointerId = event.pointerId;
  button.setPointerCapture?.(event.pointerId);

  // `passive: false` because the move handler has to preventDefault to stop the
  // page scrolling out from under the finger.
  window.addEventListener("pointermove", onLauncherPointerMove, {
    passive: false,
  });
  window.addEventListener("pointerup", onLauncherPointerUp);
  window.addEventListener("pointercancel", onLauncherPointerUp);
};

const onLauncherPointerMove = (event: PointerEvent) => {
  if (event.pointerId !== pointerId) return;

  const dx = event.clientX - grabX;
  const dy = event.clientY - grabY;

  // A tap on a touchscreen is never perfectly still. Below the threshold this
  // is still a tap, and moving the bubble a pixel under someone's thumb on the
  // way to opening it would look like a bug.
  if (!dragMoved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

  dragMoved = true;
  bubbleDragging.value = true;
  event.preventDefault();
  placeBubble(grabLeft + dx, grabTop + dy);
};

const onLauncherPointerUp = (event: PointerEvent) => {
  if (pointerId !== null && event.pointerId !== pointerId) return;

  endDragListeners();
  launcherButton.value?.releasePointerCapture?.(event.pointerId);
  pointerId = null;
  bubbleDragging.value = false;

  if (dragMoved) savePlacement();
};

/*
 * Where the panel goes, now that the bubble could be anywhere.
 *
 * It used to be a fixed offset upwards from a button that was always near the
 * bottom of the screen. Dragged to the top, that offset opens the panel above
 * the top edge; dragged to the right, past the right one. This measures.
 */
let anchor: DOMRect | null = null;

const positionSupportPopup = () => {
  const el = supportPopup.value?.$el;
  if (!el || !anchor) return;

  /*
   * Pin it to its containing block's origin and read back where that landed.
   *
   * Which element is the containing block depends on markup above it — the
   * launcher button unmounts as the panel opens, so the grid it sits in
   * reshapes at exactly the moment this runs. Measuring the origin is shorter
   * than tracking it, and cannot fall out of step with the template.
   *
   * `!important` on every one of these: `.chat-agent` and `PopUp` both set
   * these properties from a stylesheet, and `.chat-agent` sets `top` with
   * `!important`. Inline importance is the only thing that outranks it.
   */
  el.style.setProperty("right", "auto", "important");
  el.style.setProperty("bottom", "auto", "important");
  el.style.setProperty("left", "0px", "important");
  el.style.setProperty("top", "0px", "important");

  const origin = el.getBoundingClientRect();
  const { width, height } = origin;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Centred on the bubble where there is room, pushed inside the edge where not.
  const left = Math.min(
    Math.max(EDGE_GUTTER, anchor.left + anchor.width / 2 - width / 2),
    Math.max(EDGE_GUTTER, vw - width - EDGE_GUTTER),
  );

  // Above by preference — that is where it has always opened, and it leaves the
  // bubble itself uncovered — then below, then wherever it fits.
  const above = anchor.top - POPUP_GAP - height;
  const below = anchor.bottom + POPUP_GAP;
  const top =
    above >= EDGE_GUTTER
      ? above
      : below + height + EDGE_GUTTER <= vh
      ? below
      : Math.max(EDGE_GUTTER, vh - height - EDGE_GUTTER);

  el.style.setProperty("left", `${left - origin.left}px`, "important");
  el.style.setProperty("top", `${top - origin.top}px`, "important");
};

/*
 * `flush: "pre"` (the default) is load-bearing: this runs BEFORE the component
 * re-renders, which is the last moment the launcher button is still mounted and
 * therefore still measurable. After the render there is nothing left to anchor
 * to. The `nextTick` then waits for the panel to stop being `display: none`, so
 * it has a size to measure — and it is a microtask, so the styles land before
 * the browser paints and there is no frame in the wrong place.
 */
watch(showSupportPopup, async (open) => {
  if (!open) return;
  const rect = launcherButton.value?.getBoundingClientRect();
  if (rect) anchor = rect;
  await nextTick();
  positionSupportPopup();
});

const onLauncherClick = () => {
  if (dragMoved) {
    dragMoved = false;
    return;
  }
  openSupportPopup();
};

/*
 * The nudge is small enough to place with classes rather than measurement, but
 * it still has to know which way to open: from a bubble parked on the right it
 * would otherwise run off the right edge, and from one near the top an upward
 * bubble would be clipped by it.
 *
 * Each positional utility appears exactly once per branch. Listing both
 * `left-0` and `left-auto` and hoping the intended one wins is a coin flip on
 * Tailwind's output order, not on the order they are written in.
 */
const viewportWidth = ref(0);

const nudgeClasses = computed(() => {
  if (!placement.value || !viewportWidth.value) {
    // Unmoved: beside the button, on the side the button is on.
    return "bottom-full mb-3 left-0 right-auto sm:left-auto sm:right-0";
  }

  const { width, height } = bubbleSize();
  const onRightHalf =
    placement.value.left + width / 2 > viewportWidth.value / 2;
  // Roughly the nudge's own height plus its margin; below this there is not
  // room to open upwards.
  const roomAbove = placement.value.top > height + 72;

  return [
    roomAbove ? "bottom-full mb-3" : "top-full mt-3",
    onRightHalf ? "right-0" : "left-0",
  ].join(" ");
});

const onViewportResized = () => {
  viewportWidth.value = window.innerWidth;
  if (placement.value) placeBubble(placement.value.left, placement.value.top);
  if (showSupportPopup.value) positionSupportPopup();
};

onMounted(() => {
  viewportWidth.value = window.innerWidth;
  // After a tick, so the button has a measured size for the clamp to work with.
  nextTick(restorePlacement);
  window.addEventListener("resize", onViewportResized);
  window.addEventListener("orientationchange", onViewportResized);
});

onUnmounted(() => {
  endDragListeners();
  window.removeEventListener("resize", onViewportResized);
  window.removeEventListener("orientationchange", onViewportResized);
});
// Chat and Updates together — what the closed widget has to answer for.
const badgeCount = computed(() => supportStore.badgeCount);
// Whatever is being looked at full size, if anything.
const previewFile = ref<{
  url: string;
  name: string;
  mime: string | null;
} | null>(null);
const totalAttachmentBytes = computed(() => {
  return ticketForm.value.attachments.reduce(
    (total, attachment) => total + attachment.data.size,
    0,
  );
});

// Writing a message: what is typed, and what is going with it.
const chatDraft = ref("");
const pendingFiles = ref<File[]>([]);
const pendingBytes = computed(() =>
  pendingFiles.value.reduce((total, file) => total + file.size, 0),
);
/*
 * A small picture of each image or video waiting to go, so a message with
 * three screenshots on it can be checked before it is sent.
 *
 * An object URL keeps its file in memory until it is revoked, so each one is
 * made once, when the file is added, and revoked when the file leaves: removed,
 * sent, or the widget going away. A send that fails puts its files back, so
 * their pictures are kept until one succeeds.
 */
const pendingPreviews = shallowReactive(new Map<File, string>());

/*
 * A screenshot or screen recording started from the conversation.
 *
 * Set from the moment the panel starts to leave until it is back, which is
 * longer than the capture itself: the screen picker is open, and nothing is
 * being recorded yet, for as long as somebody takes to choose a screen. The
 * bubble stays away for all of it, so it can neither end up in the shot nor be
 * pressed to open the panel halfway through.
 */
const captureInProgress = ref(false);
/*
 * Who asked for the recording that is running, so it goes to the right place
 * when it ends: the message box, or the old ticket form. Null when there is
 * none, and when there is one nobody wants any more.
 */
let recordingTarget: "chat" | "ticket" | null = null;
// The size the running recording was told to stop at, for saying so if it did.
let recordingLimitBytes = 0;
const stoppingRecording = ref(false);
const recordingSeconds = ref(0);
let recordingTimer: ReturnType<typeof setInterval> | null = null;
/*
 * Screen capture is a desktop thing. Phones have no getDisplayMedia at all,
 * and a button that can only ever fail is worse than no button. Decided once
 * mounted, because there is no navigator on the server.
 */
const canCaptureScreen = ref(false);
const canRecordScreen = ref(false);
// False once the widget has gone, so a capture still in flight attaches nothing.
let widgetAlive = true;

const chatScroll = ref<HTMLElement | null>(null);
/*
 * Whether the thread has been put where it belongs.
 *
 * A conversation opens at its newest message, but the browser paints from the
 * top and the scroll happens after — which is a visible jump from the oldest
 * message down. It is held invisible for that one frame instead.
 */
const chatReady = ref(false);

// Recording a voice message.
const voiceRecorder = ref<MediaRecorder | null>(null);
const isRecordingVoice = ref(false);
const voiceSeconds = ref(0);
let voiceTimer: ReturnType<typeof setInterval> | null = null;
let voiceCancelled = false;

const voiceClock = computed(() => {
  const seconds = voiceSeconds.value;

  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
});
// Counted rather than a boolean: dragging over a child fires leave on the
// parent, and a flag would flicker the overlay off while the file is still
// over the panel.
const dragging = ref(0);

// The conversation itself.
const chat = computed(() => supportStore.chat);
const chatHasMore = computed(() => supportStore.chatHasMore);
const isLoadingChat = computed(() => supportStore.isLoadingChat);
const isLoadingOlder = computed(() => supportStore.isLoadingOlder);
const isSending = computed(() => supportStore.isSending);
const chatTickets = computed(() => supportStore.chatTickets);

/**
 * The line that closes a ticket.
 *
 * Shown under the last message of a ticket rather than above the next one, so
 * it reads in the order it happened: the exchange, then who finished it. A
 * ticket still open gets no line — nothing has happened yet to say.
 */
const closingLine = (ticketId: number, index: number) => {
  const list = chat.value;
  const isLastOfTicket =
    index === list.length - 1 || list[index + 1]?.ticket_id !== ticketId;

  if (!isLastOfTicket) {
    return null;
  }

  const ticket = chatTickets.value?.[String(ticketId)];

  if (!ticket?.resolved) {
    return null;
  }

  return {
    id: ticket.id,
    by: ticket.resolved_by,
    at: ticket.resolved_at ? formatDateTime(ticket.resolved_at) : null,
  };
};

const formatDateTime = (date: string) => {
  return new Intl.DateTimeFormat("default", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(date));
};

const openSupportPopup = async () => {
  showSupportPopup.value = true;

  /*
   * Always the conversation.
   *
   * It used to open on Updates whenever anything was unread, so a reply — the
   * commonest reason to open this at all — landed somebody on a list of
   * notices instead of on the message they came to read. Updates has its own
   * button carrying its own count, one press away.
   */
  viewMode.value = "list";
  supportStore.setPollPace(true);

  await supportStore.openChat();
  await supportStore.markChatRead();
  scrollChatToEnd();
};

/**
 * Whether to offer help right now.
 *
 * The store decides when an offer is due; this decides whether it would be
 * welcome. Never over an open panel, never while something is already waiting
 * to be read — somebody with an unread reply does not need telling that support
 * exists — and never during a capture: takeScreenshot deliberately waits for
 * this widget to leave the DOM before grabbing the frame, so a bubble appearing
 * here would end up inside the customer's own bug report. That includes the
 * stretch before the capture proper, while the panel is leaving and the screen
 * picker is open.
 */
/*
 * The halo introduces the button and then stops.
 *
 * Half a dozen pulses is enough to be noticed on arrival; carrying on for the
 * rest of the visit is how a helpful thing becomes an irritating one, and there
 * is a nudge on a timer for anybody who still has not looked.
 */
const glowing = ref(true);
let glowTimer: ReturnType<typeof setTimeout> | null = null;

const nudging = computed(
  () =>
    supportStore.nudgeDue &&
    !showSupportPopup.value &&
    !supportStore.updatesOpen &&
    !recordingState.value &&
    !screenshotingState.value &&
    !captureInProgress.value &&
    badgeCount.value === 0,
);

const dismissNudge = () => supportStore.snoozeNudge();

const takeNudgeToChat = () => {
  supportStore.snoozeNudge();
  openSupportPopup();
};

const scrollChatToEnd = () => {
  nextTick(() => {
    if (chatScroll.value) {
      chatScroll.value.scrollTop = chatScroll.value.scrollHeight;
    }

    chatReady.value = true;
  });
};

/**
 * Further back, when somebody reaches the top.
 *
 * Anchored on the height rather than a position: prepending older messages
 * moves everything below them, and without this the view jumps away from
 * whatever was being read.
 */
const onChatScroll = async () => {
  const pane = chatScroll.value;

  rememberScrollPlace();

  if (!pane || pane.scrollTop > 60 || !chatHasMore.value) {
    return;
  }

  const before = pane.scrollHeight;

  await supportStore.loadOlderChat();

  nextTick(() => {
    if (chatScroll.value) {
      chatScroll.value.scrollTop = chatScroll.value.scrollHeight - before;
    }
  });
};

/*
 * Why a file cannot go on this message, or null if it can.
 *
 * The server takes up to ten files on a message, each up to 20 MB, and turns
 * the whole message away with a 422 if any one of them is over. Finding that
 * out after the upload is the worst time to find it out. The type is not
 * checked: the conversation takes any file, unlike the old ticket form.
 */
const pendingProblem = (file: File, count: number, bytes: number) => {
  if (count >= MAX_ATTACHMENTS_COUNT) {
    return TOO_MANY_ATTACHMENTS;
  }

  if (file.size > MAX_SINGLE_ATTACHMENT_SIZE_BYTES) {
    return `'${file.name}' is larger than ${formatBytes(
      MAX_SINGLE_ATTACHMENT_SIZE_BYTES,
    )}`;
  }

  if (bytes + file.size > MAX_TOTAL_ATTACHMENTS_SIZE_BYTES) {
    return `Attachments on one message cannot add up to more than ${formatBytes(
      MAX_TOTAL_ATTACHMENTS_SIZE_BYTES,
    )}`;
  }

  return null;
};

const rememberPreview = (file: File) => {
  if (pendingPreviews.has(file) || !/^(image|video)\//.test(file.type)) {
    return;
  }

  pendingPreviews.set(file, URL.createObjectURL(file));
};

const forgetPreviews = (files: File[]) => {
  for (const file of files) {
    const url = pendingPreviews.get(file);

    // Only a file that is no longer waiting anywhere in the list gives up its
    // picture.
    if (!url || pendingFiles.value.includes(file)) {
      continue;
    }

    URL.revokeObjectURL(url);
    pendingPreviews.delete(file);
  }
};

const forgetAllPreviews = () => {
  pendingPreviews.forEach((url) => URL.revokeObjectURL(url));
  pendingPreviews.clear();
};

const openPendingPreview = (file: File) => {
  const url = pendingPreviews.get(file);

  if (url) {
    openPreview({ url, name: file.name, mime: file.type });
  }
};

/**
 * Everything that goes on the message comes through here: picked, dropped,
 * pasted, spoken or captured.
 *
 * Each file is checked as it is added, against what is already waiting and
 * what this same batch has taken so far. The ones that fit are attached and
 * the ones that do not are refused with the reason, rather than the whole
 * batch being refused for one.
 *
 * @returns how many of them were attached
 */
const addPendingFiles = (files: File[]) => {
  const accepted: File[] = [];
  const refused: string[] = [];
  let count = pendingFiles.value.length;
  let bytes = pendingBytes.value;

  for (const file of files) {
    const problem = pendingProblem(file, count, bytes);

    if (problem) {
      refused.push(problem);
      continue;
    }

    accepted.push(file);
    count += 1;
    bytes += file.size;
    rememberPreview(file);
  }

  if (accepted.length) {
    pendingFiles.value = [...pendingFiles.value, ...accepted];
  }

  if (refused.length) {
    ElMessage.warning(
      refused.length === 1
        ? refused[0]
        : `${refused[0]}. ${refused.length} files were not attached.`,
    );
  }

  return accepted.length;
};

const onPickFiles = (event: Event) => {
  const input = event.target as HTMLInputElement;

  addPendingFiles(Array.from(input.files ?? []));
  // Cleared so picking the same file twice in a row still fires.
  input.value = "";
};

const onDrop = (event: DragEvent) => {
  dragging.value = 0;
  addPendingFiles(Array.from(event.dataTransfer?.files ?? []));
};

/** A screenshot pasted straight in, which is how most of them arrive. */
const onPasteIntoChat = (event: ClipboardEvent) => {
  const file = [...(event.clipboardData?.items ?? [])]
    .find((item) => item.kind === "file")
    ?.getAsFile();

  if (!file) {
    return;
  }

  event.preventDefault();
  addPendingFiles([file]);
};

/*
 * The same paste, from anywhere on the page while the widget is open.
 *
 * The handler above only fires while the message box has focus, and a
 * screenshot is nearly always taken and then pasted without touching the box
 * first — so the commonest way to attach one did nothing at all.
 *
 * Only ever acts on a paste carrying a file, so pasting text into any other
 * field on the client's own application behaves exactly as it did.
 *
 * And never on a paste something has already dealt with. A paste into the
 * message box bubbles up to here too, already handled, and without this check
 * every screenshot pasted into the box was attached twice.
 */
const onWindowPaste = (event: ClipboardEvent) => {
  if (
    event.defaultPrevented ||
    !showSupportPopup.value ||
    viewMode.value !== "list"
  ) {
    return;
  }

  const file = [...(event.clipboardData?.items ?? [])]
    .find((item) => item.kind === "file")
    ?.getAsFile();

  if (!file) {
    return;
  }

  event.preventDefault();
  addPendingFiles([file]);
};

const removePending = (index: number) => {
  const file = pendingFiles.value[index];

  pendingFiles.value = pendingFiles.value.filter((_, i) => i !== index);

  if (file) {
    forgetPreviews([file]);
  }
};

/*
 * A name for a capture that says what it is and when it was taken.
 *
 * The composable calls every one of them screenshot.png or recording.webm, so
 * a message with three on it arrived as three files with the same name. A
 * second one inside the same second gets a number rather than a duplicate.
 */
const captureName = (prefix: string, extension: string) => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-` +
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const taken = new Set([
    ...pendingFiles.value.map((file) => file.name),
    ...ticketForm.value.attachments.map((attachment) => attachment.data.name),
  ]);

  let name = `${prefix}-${stamp}.${extension}`;

  for (let copy = 2; taken.has(name); copy += 1) {
    name = `${prefix}-${stamp}-${copy}.${extension}`;
  }

  return name;
};

/*
 * Whether a capture ended because somebody said no, rather than failing.
 *
 * Closing the screen picker, or pressing Cancel in it, rejects with
 * NotAllowedError, or AbortError in some browsers. That is somebody changing
 * their mind, and an error message for it would be telling them off.
 */
const captureCancelled = (error: unknown) => {
  const name = (error as { name?: string } | null)?.name;

  return name === "NotAllowedError" || name === "AbortError";
};

/*
 * The one NotAllowedError that is not a change of mind: macOS refusing the
 * browser permission to capture the screen at all. Chrome says "by system" in
 * the message, and the fix is in the computer's settings rather than here, so
 * it is worth saying where to look.
 */
const reportCaptureFailure = (error: unknown, failure: string) => {
  const message = String((error as { message?: string } | null)?.message);

  if (captureCancelled(error) && /system/i.test(message)) {
    ElMessage.warning(
      "Your computer is not letting this browser capture the screen. Allow it in the system's privacy settings, then try again.",
    );

    return;
  }

  if (captureCancelled(error)) {
    return;
  }

  ElMessage.error(failure);
  console.error(error);
};

/*
 * Out of the way for a capture from the conversation.
 *
 * The panel sits over the very page somebody is trying to show us, so it goes
 * before anything is grabbed, and the capture waits for it to finish leaving.
 * Reading is paused while it is gone: the store counts an open conversation as
 * read, and a reply that lands during a three-minute recording has not been.
 */
const hidePanelForCapture = async () => {
  captureInProgress.value = true;
  showSupportPopup.value = false;
  supportStore.setPollPace(false);

  await new Promise((resolve) => setTimeout(resolve, PANEL_LEAVE_MS));
};

/*
 * Back to the conversation once a capture is over, however it ended.
 *
 * Always the conversation, and always at its end, where whatever was just
 * captured is waiting under the message box. Called on every way out —
 * attached, cancelled, failed — because a panel left hidden, with the bubble
 * hidden too, is a widget that has vanished.
 *
 * A reply that landed while the panel was away is on screen again now, so it
 * is marked read here, as opening the panel does. Turning the pace back up is
 * not enough on its own: with the socket connected the poll does nothing, and
 * the reply would stay unread on the bubble after it had been seen. It costs
 * nothing when nothing arrived.
 */
const reopenAfterCapture = () => {
  captureInProgress.value = false;

  if (!widgetAlive) {
    return;
  }

  viewMode.value = "list";
  showSupportPopup.value = true;
  supportStore.setPollPace(true);
  supportStore.markChatRead();
  scrollChatToEnd();
};

/*
 * A picture of the screen, attached to the message being written.
 *
 * The share is let go as soon as the picture is taken. Keeping it open left
 * the browser saying the screen was being shared until the widget was closed,
 * for the sake of one frame.
 */
const onTakeChatScreenshot = async () => {
  if (
    captureInProgress.value ||
    recordingState.value ||
    screenshotingState.value
  ) {
    return;
  }

  // Refused before the picker rather than after it: choosing a screen only to
  // be told there was no room for the result is a waste of somebody's time.
  if (pendingFiles.value.length >= MAX_ATTACHMENTS_COUNT) {
    ElMessage.warning(TOO_MANY_ATTACHMENTS);

    return;
  }

  await hidePanelForCapture();

  try {
    const capture = await takeScreenshot();

    if (widgetAlive) {
      addPendingFiles([
        new File([capture.blob], captureName("screenshot", "png"), {
          type: "image/png",
        }),
      ]);
    }
  } catch (error) {
    reportCaptureFailure(error, "Could not take the screenshot");
  } finally {
    stopSharing();
    clearLastScreenshot();
    reopenAfterCapture();
  }
};

/*
 * Record the screen and attach it to the message being written.
 *
 * The panel is out of the shot for the length of it, and all that is left on
 * screen is the red stop control where the bubble was. It used to stay open,
 * which put the conversation — and the Stop button in it — behind the
 * "recording" state, and left only a yellow button that put the video on the
 * old ticket form, which nothing sends.
 *
 * The recording is stopped at the most this message still has room for. A
 * longer one used to record happily for as long as anybody liked and then fail
 * to send.
 *
 * It is attached by the lastVideo watcher below rather than here, because
 * there are three ways for a recording to end — the stop control, the
 * browser's own "Stop sharing" bar, and the size limit — and only one of them
 * comes through this component.
 */
const onStartRecording = async () => {
  if (
    captureInProgress.value ||
    recordingState.value ||
    screenshotingState.value
  ) {
    return;
  }

  /*
   * Not while a message is still sending. Its files are off the list until the
   * send is over, so the room worked out below would be the room on an empty
   * message. If the send then failed, its files would come back and the
   * finished recording would no longer fit, and be thrown away.
   */
  if (isSending.value) {
    ElMessage.warning(
      "Wait for the message to finish sending before recording.",
    );

    return;
  }

  if (pendingFiles.value.length >= MAX_ATTACHMENTS_COUNT) {
    ElMessage.warning(TOO_MANY_ATTACHMENTS);

    return;
  }

  const room =
    Math.min(
      MAX_SINGLE_ATTACHMENT_SIZE_BYTES,
      MAX_TOTAL_ATTACHMENTS_SIZE_BYTES - pendingBytes.value,
    ) - RECORDING_HEADROOM_BYTES;

  if (room < RECORDING_MIN_BYTES) {
    ElMessage.warning(
      "There is not enough room left on this message for a recording. Send what is attached first.",
    );

    return;
  }

  await hidePanelForCapture();

  recordingTarget = "chat";
  recordingLimitBytes = room;

  try {
    await startRecording({ maxBytes: room });
  } catch (error) {
    recordingTarget = null;
    stopSharing();
    reportCaptureFailure(error, "Could not start recording");
    reopenAfterCapture();
  }
};

/*
 * The stop control, wherever the recording was started from.
 *
 * What stopRecording resolves with is deliberately not used. The watcher below
 * attaches the recording; and when the recorder has already stopped on its
 * own, stopRecording hands back the PREVIOUS recording, which attached from
 * here would send an old video a second time.
 */
const onStopScreenRecording = async () => {
  if (stoppingRecording.value) {
    return;
  }

  stoppingRecording.value = true;

  try {
    await stopRecording();
  } finally {
    stoppingRecording.value = false;
  }
};

/*
 * A recording has finished, however it finished.
 *
 * The composable puts a new object in lastVideo each time a recorder stops, so
 * this runs once per recording whether the stop control, the browser's "Stop
 * sharing" bar or the size limit ended it. Attaching here and nowhere else is
 * what stops one recording being attached twice.
 *
 * recordingTarget is cleared the moment a recording is claimed, and before
 * every dispose() as well: dispose stops the recorder without waiting for it,
 * and the file that turns up afterwards belongs to a widget that was closed.
 */
watch(lastVideo, (video) => {
  if (!video) {
    return;
  }

  const target = recordingTarget;

  recordingTarget = null;

  if (!target || !widgetAlive) {
    clearLastVideo();

    return;
  }

  // Copied out under a proper name before the composable lets go of its own
  // URL for it. The codec is dropped from the type: it describes the stream,
  // and the server only needs to know it is a video.
  const type = (video.file.type || "video/webm").split(";")[0];
  const file = new File(
    [video.blob],
    captureName("screen-recording", type.includes("mp4") ? "mp4" : "webm"),
    { type },
  );

  clearLastVideo();

  if (target === "ticket") {
    addAttachment(
      {
        type: "video",
        data: file,
        preview: URL.createObjectURL(file),
      },
      "Video recording added successfully",
    );
    showSupportPopup.value = true;

    return;
  }

  stopSharing();

  if (!file.size) {
    ElMessage.warning("The recording came out empty, so nothing was attached");
  } else if (addPendingFiles([file]) && video.reachedLimit) {
    ElMessage.warning({
      message: `The recording stopped itself at ${formatBytes(
        recordingLimitBytes,
      )}, the most this message has room for. It is attached; record another for the rest.`,
      duration: 8000,
    });
  }

  reopenAfterCapture();
});

/*
 * The clock on the stop control. Driven by the recorder's own state, so it
 * starts and stops with the recording whichever way the recording ends.
 */
watch(recordingState, (recording) => {
  if (recordingTimer) {
    clearInterval(recordingTimer);
    recordingTimer = null;
  }

  recordingSeconds.value = 0;

  if (recording) {
    recordingTimer = setInterval(() => (recordingSeconds.value += 1), 1000);
  }
});

const recordingClock = computed(() => {
  const seconds = recordingSeconds.value;

  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
});

/**
 * Say it instead of typing it.
 *
 * Opus where the browser has it and mp4 on Safari, because passing a type the
 * browser does not support throws rather than falling back. Five minutes is the
 * ceiling: a forgotten recording should not become an hour of silence somebody
 * has to upload and somebody else has to listen to.
 */
const startVoice = async () => {
  if (isRecordingVoice.value) {
    return;
  }

  /*
   * Refused before the microphone rather than after, as a screenshot or a
   * screen recording is. The finished note goes through the same checks as
   * any other file, and five minutes of talking refused at the end is lost.
   * Not during a send either: its files are off the list until it is over, so
   * the count would be of an empty message.
   */
  if (isSending.value) {
    ElMessage.warning(
      "Wait for the message to finish sending before recording.",
    );

    return;
  }

  if (pendingFiles.value.length >= MAX_ATTACHMENTS_COUNT) {
    ElMessage.warning(TOO_MANY_ATTACHMENTS);

    return;
  }

  if (
    !navigator.mediaDevices?.getUserMedia ||
    typeof MediaRecorder === "undefined"
  ) {
    ElMessage.warning("This browser cannot record audio");

    return;
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const type = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find(
      (candidate) => MediaRecorder.isTypeSupported?.(candidate),
    );

    const recorder = new MediaRecorder(stream, type ? { mimeType: type } : {});
    const chunks: Blob[] = [];

    recorder.ondataavailable = (event) => {
      if (event.data.size) {
        chunks.push(event.data);
      }
    };

    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());

      if (voiceTimer) {
        clearInterval(voiceTimer);
        voiceTimer = null;
      }

      isRecordingVoice.value = false;
      voiceSeconds.value = 0;
      voiceRecorder.value = null;

      if (voiceCancelled) {
        voiceCancelled = false;

        return;
      }

      const blob = new Blob(chunks, {
        type: recorder.mimeType || "audio/webm",
      });

      // Anything shorter than this is a misfire, not a message.
      if (blob.size < 1024) {
        return;
      }

      const extension = (recorder.mimeType || "").includes("mp4")
        ? "m4a"
        : "webm";

      // Through the same checks as anything else: it is still one of the ten.
      addPendingFiles([
        new File([blob], `voice-message.${extension}`, { type: blob.type }),
      ]);
    };

    voiceCancelled = false;
    voiceSeconds.value = 0;
    isRecordingVoice.value = true;
    recorder.start();
    voiceRecorder.value = recorder;

    voiceTimer = setInterval(() => {
      voiceSeconds.value += 1;

      if (voiceSeconds.value >= 300) {
        stopVoice();
      }
    }, 1000);
  } catch {
    ElMessage.warning("No microphone, or permission was refused");
  }
};

const stopVoice = () => {
  if (voiceRecorder.value?.state === "recording") {
    voiceRecorder.value.stop();
  }
};

const cancelVoice = () => {
  voiceCancelled = true;
  stopVoice();
};

const sendChatMessage = async () => {
  const body = chatDraft.value.trim();

  if ((!body && !pendingFiles.value.length) || isSending.value) {
    return;
  }

  const files = pendingFiles.value;

  // Cleared first so a slow upload cannot be sent twice by an impatient
  // second press; put back only if it fails.
  chatDraft.value = "";
  pendingFiles.value = [];

  const sent = await supportStore.sendChat(body, files);

  if (!sent) {
    chatDraft.value = body;

    /*
     * Put back in front of anything added while it was trying, rather than
     * over it. Their pictures were never let go, so they come back with them.
     *
     * What was added meanwhile was checked against an empty list, so it goes
     * through the checks again on top of the files that came back. Whatever
     * no longer fits is refused with the usual reason, and its picture let go,
     * rather than leaving a message over the limit that every send would fail.
     */
    const addedMeanwhile = pendingFiles.value;

    pendingFiles.value = files;
    addPendingFiles(addedMeanwhile);
    forgetPreviews(addedMeanwhile);

    return;
  }

  // Gone for good now, so their pictures can go too.
  forgetPreviews(files);
  scrollChatToEnd();
};

/**
 * An attachment, shown here rather than in a new tab.
 *
 * A tab loses the conversation you were reading, and on a phone it loses the
 * app. Anything a browser can draw is drawn; the rest still downloads.
 */
/**
 * When a meeting is, in words.
 *
 * Said in the past tense once it has been and gone, so an old invitation in the
 * history does not read as something still to come.
 */
const meetingWhen = (meeting?: {
  starts_at?: string | null;
  minutes?: number;
}) => {
  if (!meeting?.starts_at) {
    return "Starting now";
  }

  const at = new Date(meeting.starts_at);
  const when = new Intl.DateTimeFormat("default", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(at);
  const length = meeting.minutes ? ` · ${meeting.minutes} minutes` : "";

  return `${at.getTime() < Date.now() ? "Was at" : "Starts"} ${when}${length}`;
};

/** What to call a file we never kept the name of. */
const attachmentLabel = (type?: string) =>
  ({
    image: "Photo",
    video: "Screen recording",
    audio: "Voice message",
  })[type ?? ""] ?? "Attachment";

type SentAttachment = {
  type?: string;
  name?: string;
  mime?: string | null;
  url?: string;
  payload?: { url?: string } | null;
};

/** Where an attachment's bytes are. Some messages carry it at the top level. */
const attachmentUrl = (file: SentAttachment) =>
  file?.payload?.url || file?.url || "";

/*
 * What to draw a sent attachment as.
 *
 * The server's own type first, then the mime type, so a picture that arrived
 * typed as a plain "file" is still shown as a picture. Voice messages are not
 * decided here: the player keys on the server's type alone, as it always has.
 */
const attachmentKind = (file: SentAttachment) => {
  const mime = (file?.mime || "").toLowerCase();

  if (file?.type === "image" || mime.startsWith("image/")) {
    return "image";
  }

  if (file?.type === "video" || mime.startsWith("video/")) {
    return "video";
  }

  return "file";
};

const openPreview = (file: {
  url: string;
  name: string;
  mime?: string | null;
}) => {
  previewFile.value = {
    url: file.url,
    name: file.name,
    mime: file.mime ?? null,
  };
};

const previewKind = computed(() => {
  const mime = (previewFile.value?.mime || "").toLowerCase();
  const name = (previewFile.value?.name || "").toLowerCase();

  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name))
    return "image";
  if (mime.startsWith("video/") || /\.(mp4|webm|mov|m4v)$/.test(name))
    return "video";
  if (mime.startsWith("audio/") || /\.(mp3|wav|ogg|m4a)$/.test(name))
    return "audio";
  if (mime === "application/pdf" || /\.pdf$/.test(name)) return "pdf";

  return "other";
});

const closeSupportPopup = () => {
  if (recordingState.value || screenshotingState.value) {
    return;
  }

  closeAttachmentPreview();
  supportStore.setPollPace(false);
  showSupportPopup.value = false;
  chatReady.value = false;
  viewMode.value = "list";
  supportStore.clearSelectedTicket();
};

/*
 * Everything new, on a page of its own.
 *
 * The dialog still opens by itself for whatever is unread, one at a time. This
 * is the other door: the whole list, searchable, with room for the pictures and
 * video an announcement carries — none of which belongs in a 400px panel parked
 * over somebody's work.
 *
 * The panel closes on the way out rather than being left hovering over the page
 * it has just sent them to.
 */
const openWhatsNew = async () => {
  closeSupportPopup();

  await navigateTo("/whats-new");
};

const goToListView = async () => {
  supportStore.setPollPace(true);
  viewMode.value = "list";
  supportStore.clearSelectedTicket();
  if (tickets.value.length === 0) {
    await loadTickets(1, false);
  }
};

const loadTickets = async (page = 1, append = false) => {
  if (append && !hasMoreTickets.value) {
    return;
  }

  const response = await supportStore.fetchTickets(page, append);
  currentPage.value = response.current_page;
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(2)} KB`;
  }

  return `${(kb / 1024).toFixed(2)} MB`;
};

const isMimeTypeAllowed = (file: File) => {
  return (
    ALLOWED_ATTACHMENT_MIME_TYPES.includes(file.type) ||
    ALLOWED_ATTACHMENT_MIME_PREFIXES.some((prefix) =>
      file.type.startsWith(prefix),
    )
  );
};

const validateAttachment = (file: File) => {
  if (ticketForm.value.attachments.length >= MAX_ATTACHMENTS_COUNT) {
    return `Maximum ${MAX_ATTACHMENTS_COUNT} attachments are allowed`;
  }

  if (!isMimeTypeAllowed(file)) {
    return `Unsupported file type: ${file.type || file.name}`;
  }

  if (file.size > MAX_SINGLE_ATTACHMENT_SIZE_BYTES) {
    return `File '${file.name}' is larger than ${formatBytes(
      MAX_SINGLE_ATTACHMENT_SIZE_BYTES,
    )}`;
  }

  if (
    totalAttachmentBytes.value + file.size >
    MAX_TOTAL_ATTACHMENTS_SIZE_BYTES
  ) {
    return `Total attachments size cannot exceed ${formatBytes(
      MAX_TOTAL_ATTACHMENTS_SIZE_BYTES,
    )}`;
  }

  return null;
};

const addAttachment = (
  attachment: TicketFormAttachment,
  successMessage: string,
) => {
  const validationError = validateAttachment(attachment.data);

  if (validationError) {
    if (attachment.preview) {
      URL.revokeObjectURL(attachment.preview);
    }
    ElMessage.warning(validationError);
    return false;
  }

  ticketForm.value.attachments.push(attachment);
  ElMessage.success(successMessage);
  return true;
};

// useDetectOutsideClick(formContainer, () => {
//   if (!recordingState.value && !screenshotingState.value) {
//     closeAndRemountChatBox();
//   }
// });

/**
 * The sound something arriving makes.
 *
 * Synthesised rather than shipped as a file: two soft tones, nothing to
 * download, nothing to licence, and it cannot fail to load. Browsers keep audio
 * silent until a page has been interacted with, so trying anyway would throw on
 * every notification.
 */
/*
 * The sound of something arriving.
 *
 * Played through the app's own audio service, which is what every other
 * notification in the product uses: the elements are built once at start-up from
 * the sound settings, and playing one is a name. Doing it that way means this
 * obeys the same settings as the rest — it can be changed or switched off in
 * Settings → Audio, under "Support Message" — instead of being a private sound
 * nobody can control.
 *
 * Two hand-rolled attempts came before this: a synthesised WebAudio tone, which
 * needed a context unlocked before it made any sound, and an audio element of
 * its own. Neither was heard.
 */
const playArrivalTone = () => {
  playSound("newSupportMsg");
};

/**
 * A reply that landed while the widget is open.
 *
 * Follow it down only if they were already at the bottom: yanking the view to
 * the newest message while somebody is reading further up loses their place.
 * Reaching the bottom is what reading means, so that is also where the count
 * is cleared — not merely having the widget open.
 */
/*
 * Reading is decided in the store now, on every poll, from whether the
 * conversation is on screen — see setPollPace. This is left only for the
 * scrolling, which belongs to the instance somebody is actually looking at.
 */
const onLiveMessages = () => {
  if (showSupportPopup.value && viewMode.value === "list" && wasAtBottom) {
    scrollChatToEnd();
  }
};

/*
 * Follow the conversation down as it grows.
 *
 * Watched rather than done from the poll's callback: the callback ran before
 * Vue had rendered the new message, and anything that appends — a poll, a send,
 * a reconnect — has to be followed by the same scroll. Whether to follow is
 * decided before the list changes, because afterwards the pane has already
 * grown and everybody looks like they were at the bottom.
 */
let wasAtBottom = true;

watch(
  () => chat.value.length,
  (now, before) => {
    if (now <= before || !showSupportPopup.value || viewMode.value !== "list") {
      return;
    }

    if (wasAtBottom) {
      scrollChatToEnd();
    }
  },
);

const rememberScrollPlace = () => {
  const pane = chatScroll.value;

  wasAtBottom = pane
    ? pane.scrollHeight - pane.scrollTop - pane.clientHeight < 80
    : true;
};

/*
 * Support, arriving on the socket rather than being asked for.
 *
 * These conversations live in the billing app, which has no socket to this
 * browser — so it tells our own backend and that broadcasts on the notifications
 * channel this agent is already connected to. The widget used to ask the billing
 * app every few seconds instead, from every agent of every client, whether or not
 * anybody was talking.
 */
const onPushedMessage = (payload: any) => {
  const { arrived } = supportStore.applyLive(payload ?? {});

  if (arrived) {
    playArrivalTone();
  }

  onLiveMessages();
};

/*
 * Support changed something we already have.
 *
 * No tone: a correction is not an arrival, and a sound for one would have
 * people looking for a message that is not there.
 */
const onPushedChange = (payload: any) =>
  supportStore.applyChange(payload ?? {});

const onPushedRead = (payload: any) => supportStore.applyLive(payload ?? {});

const onPushedUpdates = (payload: any) => {
  const before = supportStore.unread;

  supportStore.applyLive(payload ?? {});

  // A notice nobody has read yet is worth the same tone as a message.
  if (supportStore.unread > before) {
    playArrivalTone();
  }
};

/*
 * The socket's own state decides whether anything is asked for.
 *
 * Watched rather than read once: it drops and comes back on its own, and the
 * fallback has to follow it both ways.
 */
watch(
  () => statusStore.connectionStatus,
  (status) => supportStore.setLive(status === "Connected"),
  { immediate: true },
);

onMounted(() => {
  const { $listen } = useNuxtApp();

  canCaptureScreen.value =
    typeof navigator.mediaDevices?.getDisplayMedia === "function";
  canRecordScreen.value =
    canCaptureScreen.value && typeof MediaRecorder !== "undefined";

  document.addEventListener("paste", onWindowPaste);

  $listen("support_message_received", onPushedMessage);
  $listen("support_message_changed", onPushedChange);
  $listen("support_conversation_read", onPushedRead);
  $listen("support_updates_changed", onPushedUpdates);

  // Still started: it asks once on load, and takes over whenever the socket is
  // not carrying these — see the store.
  supportStore.startInboxPolling(playArrivalTone, onLiveMessages);
  supportStore.startNudges();

  glowTimer = setTimeout(() => (glowing.value = false), 30_000);
});

onUnmounted(() => {
  const { $unListen } = useNuxtApp();

  document.removeEventListener("paste", onWindowPaste);

  $unListen("support_message_received", onPushedMessage);
  $unListen("support_message_changed", onPushedChange);
  $unListen("support_conversation_read", onPushedRead);
  $unListen("support_updates_changed", onPushedUpdates);

  supportStore.stopInboxPolling();
  supportStore.stopNudges();

  if (glowTimer) clearTimeout(glowTimer);

  cancelVoice();
  ticketForm.value.attachments.forEach((attachment) => {
    if (attachment.preview) {
      URL.revokeObjectURL(attachment.preview);
    }
  });
  forgetAllPreviews();

  if (recordingTimer) {
    clearInterval(recordingTimer);
    recordingTimer = null;
  }

  // Before dispose(), which stops a recorder without waiting for it: nothing
  // that finishes after this is attached anywhere.
  widgetAlive = false;
  recordingTarget = null;
  dispose();
});

const closeAndRemountChatBox = async () => {
  closeSupportPopup();
  resetTicketForm();
  closeAttachmentPreview();
  // Forgotten before dispose(), which stops a recorder without waiting for it:
  // a recording that finishes afterwards belongs to a widget that was closed.
  recordingTarget = null;
  captureInProgress.value = false;
  dispose();

  chatBoxMounted.value = false;
  await nextTick();
  chatBoxRenderKey.value += 1;
  chatBoxMounted.value = true;
};

const resetTicketForm = () => {
  ticketForm.value = {
    title: "",
    problemStatement: "",
    attachments: [],
  };
};

/*
 * The same two captures, for the ticket form.
 *
 * Nothing opens that form any more — the conversation replaced it — but it is
 * still here, and a capture started from it still goes on the form's own list
 * rather than on the message. Each attachment gets a preview URL of its own:
 * the composable revokes its URL for a capture as soon as the next one is
 * taken, which used to leave the earlier attachment's preview blank.
 */
const onTakeScreenshot = async () => {
  showSupportPopup.value = false;
  // Out of the shot first, as from the conversation.
  await new Promise((resolve) => setTimeout(resolve, PANEL_LEAVE_MS));

  try {
    const capture = await takeScreenshot();
    if (capture) {
      const file = new File([capture.blob], captureName("screenshot", "png"), {
        type: "image/png",
      });

      clearLastScreenshot();
      addAttachment(
        {
          type: "screenshot",
          data: file,
          preview: URL.createObjectURL(file),
        },
        "Screenshot added successfully",
      );
    }
  } catch (error) {
    reportCaptureFailure(error, "Failed to take screenshot");
  } finally {
    showSupportPopup.value = true;
  }
};

/*
 * Stopping goes through the stop control's path, and the lastVideo watcher
 * attaches the recording to the form and brings the panel back — the same
 * whether it was stopped here, from the floating control, or from the
 * browser's "Stop sharing" bar, which used to lose the recording altogether.
 */
const onToggleVideo = async () => {
  if (recordingState.value) {
    await onStopScreenRecording();

    return;
  }

  showSupportPopup.value = false;
  recordingTarget = "ticket";

  try {
    await startRecording();
  } catch (error) {
    recordingTarget = null;
    reportCaptureFailure(error, "Failed to start video recording");
    showSupportPopup.value = true;
  }
};

const handleFileUpload = (uploadFile: any) => {
  const file = uploadFile.raw || uploadFile;
  if (file) {
    const validationError = validateAttachment(file);
    if (validationError) {
      ElMessage.warning(validationError);
      return;
    }

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const preview = isImage || isVideo ? URL.createObjectURL(file) : undefined;

    addAttachment(
      {
        type: "file",
        data: file,
        preview,
      },
      `${file.name} added successfully`,
    );
  }
};

const removeAttachment = (index: number) => {
  const attachment = ticketForm.value.attachments[index];
  if (attachment.preview) {
    URL.revokeObjectURL(attachment.preview);
  }
  ticketForm.value.attachments.splice(index, 1);
};

const openAttachmentPreview = (attachment: {
  type: "file" | "screenshot" | "video";
  data: File;
  preview?: string;
}) => {
  if (!attachment.preview) {
    ElMessage.info("No preview available for this file");
    return;
  }
  previewAttachment.value = attachment;
};

const closeAttachmentPreview = () => {
  previewAttachment.value = null;
};

const handlePaste = (event: ClipboardEvent) => {
  const items = event.clipboardData?.items;
  if (!items) return;

  for (let i = 0; i < items.length; i++) {
    if (items[i].kind === "file") {
      const file = items[i].getAsFile();
      if (file) {
        handleFileUpload({ raw: file });
      }
    }
  }
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  const files = event.dataTransfer?.files;
  if (!files) return;

  for (let i = 0; i < files.length; i++) {
    handleFileUpload({ raw: files[i] });
  }
};

const submitTicket = async () => {
  if (!ticketForm.value.title.trim()) {
    ElMessage.warning("Please enter a ticket title");
    return;
  }

  if (!ticketForm.value.problemStatement.trim()) {
    ElMessage.warning("Please enter a problem statement");
    return;
  }

  if (ticketForm.value.attachments.length > MAX_ATTACHMENTS_COUNT) {
    ElMessage.warning(
      `Maximum ${MAX_ATTACHMENTS_COUNT} attachments are allowed`,
    );
    return;
  }

  if (totalAttachmentBytes.value > MAX_TOTAL_ATTACHMENTS_SIZE_BYTES) {
    ElMessage.warning(
      `Total attachments size cannot exceed ${formatBytes(
        MAX_TOTAL_ATTACHMENTS_SIZE_BYTES,
      )}`,
    );
    return;
  }

  try {
    await supportStore.createTicket({
      title: ticketForm.value.title.trim(),
      body: ticketForm.value.problemStatement.trim(),
      metadata: {
        origin_url: ticketOriginUrl.value,
      },
      attachments: ticketForm.value.attachments.map(
        (attachment) => attachment.data,
      ),
    });

    ElMessage.success("Ticket created successfully");
    resetTicketForm();
    viewMode.value = "list";
    await loadTickets(1, false);
  } catch (error) {
    console.error(error);
  }
};
</script>

<template>
  <div
    v-if="chatBoxMounted"
    :key="chatBoxRenderKey"
    class="chat-launcher fixed z-[100] sm:left-auto sm:right-[20px] sm:bottom-[20px]"
    :class="
      placement
        ? 'chat-launcher--placed'
        : 'left-[20px] right-auto bottom-[150px]'
    "
    :style="
      placement
        ? {
            left: `${placement.left}px`,
            top: `${placement.top}px`,
            right: 'auto',
            bottom: 'auto',
          }
        : undefined
    "
  >
    <div ref="formContainer" class="relative grid">
      <!--
        The launcher, and the drag handle.

        `touch-none` is what makes the drag work on a phone: without it the
        browser claims the gesture as a page scroll before preventDefault gets a
        say. It is on the BUTTON rather than on the container, because the
        container also holds the panel, and the panel's message list has to
        scroll.

        `@dragstart.prevent` is the same problem with a mouse. SVG and IMG
        elements are natively draggable, so a slow press on the headset glyph
        started an HTML5 drag: the browser tore a translucent copy of the icon
        out of the button and followed the cursor with it, while the bubble
        itself stayed put. `select-none` does not prevent that — it governs text
        selection, not dragging — and neither does `touch-action`, which pointer
        events never consult on a mouse. Cancelling `dragstart` does, and it is
        on the button because the event bubbles up from whichever child the
        press actually landed on.
      -->
      <button
        v-if="
          !showSupportPopup &&
          !recordingState &&
          !screenshotingState &&
          !captureInProgress
        "
        ref="launcherButton"
        class="relative block bg-light-purple rounded-full shadow-lg p-3 dark:bg-light-purple-bar transition-all hover:bg-light-sec-purple hover:shadow-xl hover:ring-8 hover:ring-light-purple/20 touch-none select-none cursor-grab active:cursor-grabbing"
        :class="[
          nudging && 'ring-4 ring-light-sec-purple/40',
          bubbleDragging ? 'scale-110 shadow-2xl' : 'hover:scale-110',
        ]"
        :title="
          unread > 0
            ? `${unread} update${unread === 1 ? '' : 's'} waiting`
            : 'Need Support? Create a ticket — drag to move it'
        "
        @dragstart.prevent
        @pointerdown="onLauncherPointerDown"
        @click="onLauncherClick"
      >
        <!--
          A slow halo, on its own rather than under a cursor.

          Reacting only to hover is no use to somebody who has not noticed the
          button in the first place. Three seconds a cycle at low opacity: enough
          movement to catch the eye at the edge of vision, not enough to sit in
          the way of somebody working. Nothing at all for anybody who has asked
          their system for less motion.
        -->
        <span
          v-if="glowing"
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 rounded-full bg-light-purple dark:bg-light-purple-bar opacity-60 animate-ping [animation-duration:3s] motion-reduce:hidden"
        />

        <!--
          And afterwards, a light that runs round the edge.

          The halo says "here I am" on arrival; this keeps the button findable
          without repeating that every three seconds. An arc rather than a full
          ring, turning slowly — it reads as something alive to come back to,
          not as something loading that anybody should wait for.
        -->
        <svg
          v-else
          aria-hidden="true"
          viewBox="0 0 100 100"
          fill="none"
          class="pointer-events-none absolute -inset-1 animate-spin [animation-duration:3.5s] text-light-purple dark:text-light-purple-bar motion-reduce:hidden"
        >
          <circle
            cx="50"
            cy="50"
            r="46"
            stroke="currentColor"
            stroke-width="7"
            stroke-linecap="round"
            stroke-dasharray="70 220"
            class="opacity-70"
          />
        </svg>

        <img
          alt=""
          class="relative invert w-[20px] object-cover self-center mx-auto"
          src="/images/support.png"
        />

        <!--
          The count, where it can be seen without opening anything.

          A notification only anybody who already opened the widget can see is
          not a notification.
        -->
        <span
          v-if="badgeCount > 0"
          class="absolute -top-1 -right-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-dark-blue"
          >{{ badgeCount > 99 ? "99+" : badgeCount }}</span
        >
      </button>

      <!--
        The stop control, for as long as the screen is being recorded.

        It is the only part of the widget on screen while the panel is out of
        the shot, so it says plainly what it does: red, a stop square, and how
        long it has been going. It used to be a yellow "pause" button that did
        not pause anything. Stopping here and stopping from the browser's own
        "Stop sharing" bar come to the same thing: the recording is attached
        and the panel comes back. The square comes first, so it stays in view
        even when the bubble was parked hard against the right-hand edge.
      -->
      <button
        v-if="recordingState"
        type="button"
        class="flex items-center gap-2 rounded-full bg-red-600 py-2.5 pl-3 pr-4 text-white shadow-lg ring-4 ring-red-600/20 transition-all hover:bg-red-700 disabled:opacity-60"
        title="Stop recording"
        :disabled="stoppingRecording"
        @click="onStopScreenRecording"
      >
        <svg
          class="h-4 w-4 shrink-0"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <rect x="5" y="5" width="14" height="14" rx="2" />
        </svg>
        <span class="text-xs font-bold">Stop</span>
        <span
          class="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-white"
          aria-hidden="true"
        />
        <span class="text-xs font-semibold tabular-nums">{{
          recordingClock
        }}</span>
      </button>

      <!--
        The offer, made now and then rather than waited for.

        Sits beside the button on the side the button is on, so it never runs off
        the screen: bottom-left on a phone, bottom-right from sm up.
      -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="nudging"
          class="absolute w-max max-w-[15rem]"
          :class="nudgeClasses"
        >
          <div
            class="flex items-start gap-2 rounded-2xl bg-white px-3 py-2 shadow-xl ring-1 ring-black/5 dark:bg-dark-blue dark:ring-white/10"
          >
            <button
              type="button"
              class="text-left text-xs font-semibold text-light-purple dark:text-light-purple-bar"
              @click="takeNudgeToChat"
            >
              Need help? Talk to a support agent now.
            </button>

            <button
              type="button"
              aria-label="Not now"
              class="shrink-0 rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              @click="dismissNudge"
            >
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12 19 6.4Z"
                />
              </svg>
            </button>
          </div>
        </div>
      </transition>

      <TransWrap :show="showSupportPopup">
        <!--
          The ! modifiers are on purpose. PopUp bakes right-[0px] into the shared
          component, so a plain utility here is a coin flip on Tailwind's class
          order — main.css records this exact override being lost once already.
        -->
        <PopUp
          v-show="showSupportPopup"
          ref="supportPopup"
          class="chat-agent shadow-lg chat-width dark:bg-dark-blue !left-0 !right-auto sm:!left-auto sm:!right-[44px] max-w-[calc(100vw-2.5rem)]"
        >
          <div
            class="flex w-full min-w-0 flex-col gap-4 overflow-hidden p-4 h-[400px] min-h-0"
          >
            <div
              class="flex shrink-0 justify-between items-center pb-3 border-b"
            >
              <h2 class="text-lg font-semibold dark:text-white">
                <template v-if="recordingState">Recording...</template>
                <template v-else-if="screenshotingState">Capturing...</template>
                <template v-else-if="viewMode === 'list'">Inbox</template>
                <template v-else>Create Support Ticket</template>
              </h2>

              <div class="flex items-center gap-2">
                <!-- Updates, with what is waiting on it. -->
                <button
                  v-if="
                    !recordingState &&
                    !screenshotingState &&
                    viewMode === 'list'
                  "
                  class="relative text-sm text-light-purple dark:text-light-purple-bar font-medium"
                  title="See everything new on its own page"
                  @click="openWhatsNew"
                >
                  Updates
                  <span
                    v-if="unread > 0"
                    class="absolute -top-2 -right-3 min-w-[16px] rounded-full bg-red-500 px-1 text-[10px] font-bold leading-4 text-white"
                    >{{ unread > 99 ? "99+" : unread }}</span
                  >
                </button>

                <button
                  v-if="
                    !recordingState &&
                    !screenshotingState &&
                    viewMode !== 'list'
                  "
                  class="text-sm text-light-purple dark:text-light-purple-bar font-medium"
                  @click="goToListView()"
                >
                  Back
                </button>

                <!--
                  No "new ticket" button, by design. A conversation opens a
                  ticket on the first message and joins the open one after
                  that; a second is only started once the last was
                  resolved. Offering a button here would let somebody split
                  one problem across three tickets.
                -->

                <button
                  v-if="!recordingState && !screenshotingState"
                  class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  @click="closeAndRemountChatBox"
                >
                  <Svgs name="close" class="w-5" />
                </button>
              </div>
            </div>

            <!-- The conversation. What the widget opens on. -->
            <div
              v-if="
                viewMode === 'list' && !recordingState && !screenshotingState
              "
              class="flex min-h-0 flex-1 flex-col gap-2"
              @dragenter.prevent="dragging++"
              @dragover.prevent
              @dragleave.prevent="dragging--"
              @drop.prevent="onDrop"
            >
              <!-- Only while something is actually over the panel -->
              <div
                v-if="dragging > 0"
                class="absolute inset-2 z-20 grid place-content-center rounded-xl border-2 border-dashed border-light-purple bg-light-purple/10 text-sm font-semibold text-light-purple"
              >
                Drop to attach
              </div>

              <div
                v-if="isLoadingChat && !chat.length"
                class="flex-1 grid place-content-center text-sm text-gray-500 dark:text-gray-300"
              >
                Loading...
              </div>

              <!--
                min-h-0 matters: without it flexbox sizes this to its content
                rather than letting it shrink and scroll, and the composer is
                pushed off the bottom.
              -->
              <div
                v-else
                ref="chatScroll"
                class="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 transition-opacity"
                :class="chatReady ? 'opacity-100' : 'opacity-0'"
                @scroll="onChatScroll"
              >
                <p
                  v-if="isLoadingOlder"
                  class="py-1 text-center text-[10px] text-gray-400"
                >
                  Loading earlier...
                </p>
                <p
                  v-else-if="!chatHasMore && chat.length"
                  class="py-1 text-center text-[10px] text-gray-300"
                >
                  This is the beginning.
                </p>

                <div
                  v-if="!chat.length"
                  class="grid h-full place-content-center px-6 text-center"
                >
                  <p class="text-sm font-semibold dark:text-white">
                    How can we help?
                  </p>
                  <p class="mt-1 text-xs text-gray-500 dark:text-gray-300">
                    Write below and somebody will pick it up. Attach a file,
                    take a screenshot or record your screen if it is easier to
                    show.
                  </p>
                </div>

                <template v-else>
                  <template v-for="(message, index) in chat" :key="message.id">
                    <!--
                    Where one ticket ended and the next began. The conversation
                    is continuous; this is only a marker in it.
                  -->
                    <p
                      v-if="
                        index > 0 &&
                        message.ticket_id !== chat[index - 1].ticket_id
                      "
                      class="py-2 text-center text-[10px] uppercase tracking-wide text-gray-300"
                    >
                      Earlier conversation
                    </p>

                    <div
                      class="flex"
                      :class="message.mine ? 'justify-end' : 'justify-start'"
                    >
                      <div class="max-w-[80%]">
                        <p
                          v-if="!message.mine"
                          class="mb-0.5 text-[10px] font-semibold text-gray-500 dark:text-gray-400"
                        >
                          {{ message.from }}
                        </p>

                        <div
                          class="rounded-2xl px-3 py-2 text-sm"
                          :class="
                            message.mine
                              ? 'rounded-br-sm bg-light-purple text-white'
                              : 'rounded-bl-sm bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
                          "
                        >
                          <!--
                          What it answers, quoted inside the bubble.

                          The spine and the tint are the shape every messaging
                          app uses for a quote, so it reads as one message about
                          another rather than as two messages.
                        -->
                          <div
                            v-if="message.reply_to"
                            class="mb-1 flex gap-2 overflow-hidden rounded-lg px-2 py-1.5"
                            :class="
                              message.mine
                                ? 'bg-white/15'
                                : 'bg-gray-200/70 dark:bg-gray-600/60'
                            "
                          >
                            <span
                              class="w-0.5 shrink-0 rounded-full"
                              :class="
                                message.mine ? 'bg-white/70' : 'bg-light-purple'
                              "
                            ></span>
                            <span class="min-w-0 flex-1">
                              <span
                                class="block truncate text-[11px] font-bold"
                                :class="
                                  message.mine
                                    ? 'text-white/90'
                                    : 'text-light-purple'
                                "
                              >
                                {{ message.reply_to.sender }}
                              </span>
                              <span
                                class="block truncate text-[11px]"
                                :class="
                                  message.mine
                                    ? 'text-white/70'
                                    : 'text-gray-500 dark:text-gray-300'
                                "
                              >
                                {{ message.reply_to.body || "Attachment" }}
                              </span>
                            </span>
                          </div>

                          <!--
                          Withdrawn.

                          The message keeps its place and loses its words: a
                          conversation with a hole in it leaves the reader
                          wondering what they missed, which is worse than being
                          told something was taken back.
                        -->
                          <p
                            v-if="message.deleted"
                            class="flex items-center gap-1.5 whitespace-pre-wrap break-words italic opacity-70"
                          >
                            <svg
                              class="h-3.5 w-3.5 shrink-0"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path
                                d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 18a8 8 0 0 1-6.32-12.9l11.22 11.22A7.96 7.96 0 0 1 12 20Zm6.32-3.1L7.1 5.68A8 8 0 0 1 18.32 16.9Z"
                              />
                            </svg>
                            This message was deleted
                          </p>

                          <!--
                          An address in the words is something to press.

                          The meeting card below is a different thing and is
                          untouched. This is for a bare link sitting in the
                          text — a Google Meet address pasted in by the
                          scheduler used to arrive as letters nobody could
                          click, leaving people to select it by hand.

                          The body is split into text and links rather than
                          turned into markup, and every piece is rendered
                          below as a text node. A support message is written
                          by whoever is on the other end of the conversation,
                          so no HTML is ever built out of one.
                        -->
                          <p
                            v-else-if="message.body"
                            class="whitespace-pre-wrap break-words"
                          >
                            <template
                              v-for="(
                                segment, segmentIndex
                              ) in splitIntoLinkSegments(message.body)"
                              :key="segmentIndex"
                            >
                              <!--
                                break-all rather than the bubble's break-words:
                                the bubble is narrow, and a long address is one
                                unbroken word that would otherwise push it wider
                                than the panel. Nothing else in the bubble reacts
                                to the press.
                              -->
                              <a
                                v-if="segment.href"
                                :href="segment.href"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="break-all underline underline-offset-2"
                                :class="
                                  message.mine
                                    ? 'text-white'
                                    : 'text-light-purple dark:text-indigo-300'
                                "
                                @click.stop
                                >{{ segment.text }}</a
                              >
                              <template v-else>{{ segment.text }}</template>
                            </template>
                          </p>

                          <!--
                          A meeting, as something to join.

                          It used to arrive as three lines of text with the link
                          in the middle, while the same meeting sent between
                          colleagues drew a card with a button on it.
                        -->
                          <a
                            v-if="message.meeting?.url"
                            :href="message.meeting.url"
                            target="_blank"
                            rel="noopener"
                            class="mt-1.5 flex items-center gap-2 rounded-xl px-2.5 py-2 no-underline transition"
                            :class="
                              message.mine
                                ? 'bg-white/20 hover:bg-white/30'
                                : 'bg-white hover:bg-gray-50 dark:bg-gray-800'
                            "
                          >
                            <span
                              class="grid h-8 w-8 shrink-0 place-items-center rounded-lg"
                              :class="
                                message.mine
                                  ? 'bg-white/25'
                                  : 'bg-light-purple/10 text-light-purple'
                              "
                            >
                              <svg
                                class="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path
                                  d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4Z"
                                />
                              </svg>
                            </span>

                            <span class="min-w-0 flex-1">
                              <span class="block text-xs font-semibold"
                                >Join the meeting</span
                              >
                              <span
                                class="block text-[10px]"
                                :class="
                                  message.mine
                                    ? 'text-white/80'
                                    : 'text-gray-500 dark:text-gray-300'
                                "
                                >{{ meetingWhen(message.meeting) }}</span
                              >
                            </span>
                          </a>

                          <template
                            v-for="(file, fileIndex) in message.attachments"
                            :key="fileIndex"
                          >
                            <!--
                            A voice message plays where it sits.

                            Anything else opens full size, but sound has nothing
                            to show: making somebody open a window to hear two
                            spoken words is not a voice message.
                          -->
                            <audio
                              v-if="file.type === 'audio'"
                              :src="file.payload?.url || file.url"
                              controls
                              preload="none"
                              class="mt-1 h-9 w-[13rem] max-w-full"
                            />

                            <!--
                            A picture shows as itself, and opens full size.

                            In a box of fixed size rather than the picture's own
                            shape: the thread is scrolled to its end before any
                            image has loaded, and one that grew as it arrived
                            would push the newest message back out of sight.
                          -->
                            <button
                              v-else-if="
                                attachmentKind(file) === 'image' &&
                                attachmentUrl(file)
                              "
                              type="button"
                              class="mt-1 block h-32 w-48 max-w-full overflow-hidden rounded-lg"
                              :class="
                                message.mine
                                  ? 'bg-white/20'
                                  : 'bg-white dark:bg-gray-800'
                              "
                              :title="file.name || attachmentLabel(file.type)"
                              @click="
                                openPreview({
                                  url: attachmentUrl(file),
                                  name: file.name || attachmentLabel(file.type),
                                  mime: file.mime,
                                })
                              "
                            >
                              <img
                                :src="attachmentUrl(file)"
                                :alt="file.name || attachmentLabel(file.type)"
                                loading="lazy"
                                class="h-full w-full object-cover"
                              />
                            </button>

                            <!--
                            A video as a tile to press rather than a player in
                            the bubble: a player would start fetching every
                            recording in the history as the thread scrolled past.
                          -->
                            <button
                              v-else-if="
                                attachmentKind(file) === 'video' &&
                                attachmentUrl(file)
                              "
                              type="button"
                              class="mt-1 flex h-28 w-48 max-w-full flex-col items-center justify-center gap-1.5 overflow-hidden rounded-lg bg-gray-900 text-white hover:bg-gray-800"
                              :title="file.name || attachmentLabel(file.type)"
                              @click="
                                openPreview({
                                  url: attachmentUrl(file),
                                  name: file.name || attachmentLabel(file.type),
                                  mime: file.mime,
                                })
                              "
                            >
                              <span
                                class="grid h-10 w-10 place-items-center rounded-full bg-white/20"
                              >
                                <svg
                                  class="ml-0.5 h-5 w-5"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path d="M8 5v14l11-7L8 5Z" />
                                </svg>
                              </span>
                              <span
                                class="max-w-full truncate px-2 text-[10px] text-white/80"
                                >{{
                                  file.name || attachmentLabel(file.type)
                                }}</span
                              >
                            </button>

                            <button
                              v-else
                              class="mt-1 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs"
                              :class="
                                message.mine
                                  ? 'bg-white/20 hover:bg-white/30'
                                  : 'bg-white hover:bg-gray-50 dark:bg-gray-800'
                              "
                              @click="
                                openPreview({
                                  url: file.payload?.url || file.url,
                                  name: file.name || attachmentLabel(file.type),
                                  mime: file.mime,
                                })
                              "
                            >
                              <span class="truncate">{{
                                file.name || attachmentLabel(file.type)
                              }}</span>
                            </button>
                          </template>
                        </div>

                        <p
                          class="mt-0.5 flex items-center gap-1 text-[10px] text-gray-400"
                          :class="message.mine && 'justify-end'"
                        >
                          <span>{{
                            message.created_at
                              ? formatDateTime(message.created_at)
                              : ""
                          }}</span>

                          <!--
                          Changed after it was sent.

                          Said rather than left to be noticed: a message that
                          quietly rewrites itself is worse than one that admits
                          it was corrected. Not shown on a withdrawn message,
                          where the withdrawal is the point.
                        -->
                          <span
                            v-if="message.edited && !message.deleted"
                            class="italic"
                            title="This message was edited"
                            >edited</span
                          >

                          <!-- One tick sent, two read. Our own messages only. -->
                          <span
                            v-if="message.mine"
                            class="inline-flex"
                            :class="
                              message.read_at ? 'text-sky-500' : 'text-gray-400'
                            "
                            :title="message.read_at ? 'Read' : 'Sent'"
                          >
                            <svg
                              class="h-3.5 w-3.5"
                              viewBox="0 0 20 16"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M1 8.5 5 12.5 12 3" />
                              <path
                                v-if="message.read_at"
                                d="M8 8.5 9.5 10 18 3"
                              />
                            </svg>
                            <span class="sr-only">{{
                              message.read_at ? "Read" : "Sent"
                            }}</span>
                          </span>
                        </p>
                      </div>
                    </div>

                    <!--
                    What became of that ticket. Under its last message, so the
                    thread reads in the order things happened.
                  -->
                    <div
                      v-if="closingLine(message.ticket_id, index)"
                      class="flex items-center gap-2 py-3"
                    >
                      <span class="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                      <span
                        class="rounded-sm bg-[#F4F1FF] px-2 py-1 text-center text-[10px] text-[#5C5C80] dark:bg-gray-800 dark:text-gray-300"
                      >
                        Ticket
                        <span class="text-[#5A55CE]">
                          #{{ closingLine(message.ticket_id, index).id }}
                        </span>
                        was resolved
                        <template
                          v-if="closingLine(message.ticket_id, index).by"
                        >
                          by
                          <span class="text-[#5A55CE]">
                            {{ closingLine(message.ticket_id, index).by }}
                          </span>
                        </template>
                        <template
                          v-if="closingLine(message.ticket_id, index).at"
                        >
                          at
                          <span class="text-[#5A55CE]">
                            {{ closingLine(message.ticket_id, index).at }}
                          </span>
                        </template>
                      </span>
                      <span class="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                    </div>
                  </template>
                </template>
              </div>

              <!--
                Writing. There is no form and no title: the first message opens
                a ticket, and everything after joins it. A new one only begins
                once the last was resolved, which is decided on the server.
              -->
              <div class="shrink-0 border-t pt-2 dark:border-gray-700">
                <div
                  v-if="pendingFiles.length"
                  class="mb-2 flex flex-wrap gap-1"
                >
                  <span
                    v-for="(file, index) in pendingFiles"
                    :key="index"
                    class="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-1 pr-1 text-[10px] dark:bg-gray-700 dark:text-gray-200"
                  >
                    <!--
                      A picture of it, for anything that has one, and a press to
                      see it full size before it goes.
                    -->
                    <button
                      v-if="pendingPreviews.get(file)"
                      type="button"
                      class="flex min-w-0 items-center gap-1 rounded-full hover:opacity-80"
                      :title="`Look at ${file.name}`"
                      @click="openPendingPreview(file)"
                    >
                      <img
                        v-if="file.type.startsWith('image/')"
                        :src="pendingPreviews.get(file)"
                        alt=""
                        class="h-6 w-6 shrink-0 rounded-full object-cover"
                      />
                      <span
                        v-else
                        class="relative grid h-6 w-6 shrink-0 place-items-center overflow-hidden rounded-full bg-gray-900"
                      >
                        <video
                          :src="pendingPreviews.get(file)"
                          muted
                          playsinline
                          preload="metadata"
                          class="absolute inset-0 h-full w-full object-cover"
                        />
                        <svg
                          class="relative h-3 w-3 text-white"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M8 5v14l11-7L8 5Z" />
                        </svg>
                      </span>
                      <span class="truncate">{{ file.name }}</span>
                    </button>
                    <span v-else class="truncate pl-1">{{ file.name }}</span>

                    <button
                      class="rounded-full px-1 hover:bg-gray-200 dark:hover:bg-gray-600"
                      @click="removePending(index)"
                    >
                      ×
                    </button>
                  </span>
                </div>

                <!--
                  No "recording your screen" row here any more. The panel is out
                  of the shot for the whole of a recording, so a Stop button in
                  it could never be pressed; the stop control is the one that
                  takes the bubble's place.
                -->

                <div class="flex items-end gap-1.5">
                  <input
                    ref="chatFiles"
                    type="file"
                    multiple
                    class="hidden"
                    @change="onPickFiles"
                  />

                  <!--
                    The tooltips say what each button does, because an outline
                    of a camera is not self-explanatory to somebody who has
                    never pressed it. Element Plus puts the bubble on the body
                    rather than in the panel, so the panel's own overflow
                    cannot clip it.

                    The v-if sits on the tooltip rather than the button: a
                    tooltip whose only child has gone has nothing left to hang
                    on. The conditions themselves are unchanged. The words are
                    repeated as an aria-label because the tooltip replaced the
                    title attribute, and a button drawn as an icon has no other
                    name for a screen reader to read.
                  -->
                  <el-tooltip
                    v-if="!isRecordingVoice"
                    content="Attach a file"
                    placement="top"
                  >
                    <button
                      class="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                      aria-label="Attach a file"
                      @click="$refs.chatFiles.click()"
                    >
                      <svg
                        class="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M16.5 6v11.5a4.5 4.5 0 0 1-9 0V5a3 3 0 0 1 6 0v10.5a1.5 1.5 0 0 1-3 0V6H9v9.5a3 3 0 0 0 6 0V5a4.5 4.5 0 0 0-9 0v12.5a6 6 0 0 0 12 0V6h-1.5Z"
                        />
                      </svg>
                    </button>
                  </el-tooltip>

                  <!--
                    A picture of the screen, straight onto the message. The
                    panel gets out of the way first, so it is not in the shot,
                    and comes back with the picture waiting above the box.
                  -->
                  <el-tooltip
                    v-if="
                      canCaptureScreen && !recordingState && !isRecordingVoice
                    "
                    content="Press to take a screenshot of your screen"
                    placement="top"
                  >
                    <button
                      class="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                      aria-label="Press to take a screenshot of your screen"
                      :disabled="screenshotingState || captureInProgress"
                      @click="onTakeChatScreenshot"
                    >
                      <svg
                        class="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z"
                        />
                        <path
                          d="M9 2 7.17 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3.17L15 2H9Zm3 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z"
                        />
                      </svg>
                    </button>
                  </el-tooltip>

                  <el-tooltip
                    v-if="
                      canRecordScreen && !recordingState && !isRecordingVoice
                    "
                    content="Press to record your screen"
                    placement="top"
                  >
                    <button
                      class="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                      aria-label="Press to record your screen"
                      :disabled="screenshotingState || captureInProgress"
                      @click="onStartRecording"
                    >
                      <svg
                        class="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4Z"
                        />
                      </svg>
                    </button>
                  </el-tooltip>

                  <!--
                    Say it instead of typing it.

                    While recording the row becomes the recorder: a running
                    clock, a way to throw it away, and a way to keep it. Leaving
                    the text box there invites typing into a message that is
                    already being spoken.
                  -->
                  <el-tooltip
                    v-if="!recordingState && !isRecordingVoice"
                    content="Press to record a voice message"
                    placement="top"
                  >
                    <button
                      class="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                      aria-label="Press to record a voice message"
                      @click="startVoice"
                    >
                      <svg
                        class="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-2.08A7 7 0 0 0 19 12h-2Z"
                        />
                      </svg>
                    </button>
                  </el-tooltip>

                  <template v-if="isRecordingVoice">
                    <div
                      class="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900/50 dark:bg-red-950/30"
                    >
                      <span
                        class="h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-500"
                      />
                      <span
                        class="text-sm font-semibold tabular-nums text-red-600 dark:text-red-300"
                        >{{ voiceClock }}</span
                      >
                      <span class="truncate text-xs text-red-500/80"
                        >Recording…</span
                      >
                    </div>

                    <button
                      class="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                      title="Throw it away"
                      @click="cancelVoice"
                    >
                      <svg
                        class="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41Z"
                        />
                      </svg>
                    </button>

                    <button
                      class="shrink-0 rounded-xl bg-light-purple p-2 text-white"
                      title="Keep it"
                      @click="stopVoice"
                    >
                      <svg
                        class="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17Z"
                        />
                      </svg>
                    </button>
                  </template>

                  <textarea
                    v-else
                    v-model="chatDraft"
                    rows="1"
                    placeholder="Write a message..."
                    class="max-h-24 w-0 min-w-0 flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-light-purple focus:outline-none dark:border-gray-700 dark:bg-dark-blue dark:text-white"
                    @keydown.enter.exact.prevent="sendChatMessage"
                    @paste="onPasteIntoChat"
                  />

                  <button
                    v-if="!isRecordingVoice"
                    class="shrink-0 rounded-xl bg-light-purple p-2 text-white disabled:opacity-40"
                    :disabled="
                      (!chatDraft.trim() && !pendingFiles.length) || isSending
                    "
                    @click="sendChatMessage"
                  >
                    <svg
                      class="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M2 21 23 12 2 3v7l15 2-15 2v7Z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Create Ticket View -->
            <div
              v-else-if="viewMode === 'create'"
              class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1 relative"
            >
              <div
                v-if="!recordingState && !screenshotingState"
                class="flex flex-col gap-2"
              >
                <label class="text-sm font-medium dark:text-gray-200">
                  Title
                </label>
                <el-input
                  v-model="ticketForm.title"
                  placeholder="Enter ticket title"
                  class="dark:bg-gray-800"
                />
              </div>

              <div
                v-if="!recordingState && !screenshotingState"
                class="flex flex-col gap-2"
              >
                <label class="text-sm font-medium dark:text-gray-200">
                  Problem Statement
                </label>
                <el-input
                  v-model="ticketForm.problemStatement"
                  type="textarea"
                  :rows="4"
                  placeholder="Describe your issue in detail"
                  class="dark:bg-gray-800"
                />
              </div>

              <div
                v-if="!recordingState && !screenshotingState"
                class="flex flex-col gap-2"
              >
                <label class="text-sm font-medium dark:text-gray-200">
                  Attachments
                </label>

                <div
                  class="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  @drop="handleDrop"
                  @dragover.prevent
                  @paste="handlePaste"
                >
                  <div class="flex flex-col gap-3">
                    <button
                      type="button"
                      :disabled="screenshotingState"
                      class="flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 rounded-md text-sm font-medium text-blue-700 dark:text-blue-200 disabled:opacity-50"
                      @click="onTakeScreenshot"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-4 h-4"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6.375 23.5h11.25A2.625 2.625 0 0020.25 20.875V3.125A2.625 2.625 0 0017.625.5H6.375A2.625 2.625 0 003.75 3.125v17.75A2.625 2.625 0 006.375 23.5z"
                        />
                      </svg>
                      {{
                        screenshotingState ? "Capturing..." : "Take Screenshot"
                      }}
                    </button>

                    <button
                      type="button"
                      :class="{
                        'bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-700 dark:text-red-200':
                          recordingState,
                        'bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-700 dark:text-green-200':
                          !recordingState,
                      }"
                      class="flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
                      @click="onToggleVideo"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        :fill="recordingState ? 'currentColor' : 'none'"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-4 h-4"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M16.5 12a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm0 0c0 1.657-.895 3.253-2.457 4.338 1.503-.976 2.457-2.524 2.457-4.338z"
                        />
                      </svg>
                      {{
                        recordingState ? "Stop Recording" : "Start Recording"
                      }}
                    </button>

                    <el-upload
                      action="#"
                      :auto-upload="false"
                      multiple
                      @change="handleFileUpload"
                    >
                      <template #trigger>
                        <button
                          type="button"
                          class="flex items-center justify-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 rounded-md text-sm font-medium text-purple-700 dark:text-purple-200"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33A3 3 0 0116.5 19.5H6.75z"
                            />
                          </svg>
                          Upload Files
                        </button>
                      </template>
                    </el-upload>

                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Drag and drop files or paste images here
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-if="ticketForm.attachments.length > 0"
                class="flex flex-col gap-2"
              >
                <p class="text-xs font-medium text-gray-600 dark:text-gray-300">
                  {{ ticketForm.attachments.length }} attachment(s)
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(attachment, index) in ticketForm.attachments"
                    :key="index"
                    class="flex items-center justify-between gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md"
                  >
                    <button
                      type="button"
                      class="flex items-center gap-2 flex-1 min-w-0 text-left"
                      @click="openAttachmentPreview(attachment)"
                    >
                      <div
                        v-if="attachment.preview"
                        class="w-8 h-8 rounded flex-shrink-0"
                      >
                        <img
                          v-if="attachment.type === 'screenshot'"
                          :src="attachment.preview"
                          alt="Screenshot"
                          class="w-full h-full object-cover rounded"
                        />
                        <video
                          v-else-if="attachment.type === 'video'"
                          :src="attachment.preview"
                          class="w-full h-full object-cover rounded"
                        />
                      </div>

                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium dark:text-white truncate">
                          {{ attachment.data.name }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                          {{ (attachment.data.size / 1024).toFixed(2) }}KB
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      class="text-red-500 hover:text-red-700 dark:hover:text-red-300 flex-shrink-0"
                      @click.stop="removeAttachment(index)"
                    >
                      <Svgs name="close" class="w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div
                v-if="!recordingState && !screenshotingState"
                class="feature-filter-submit sticky"
              >
                <button
                  class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  @click="goToListView"
                >
                  Cancel
                </button>

                <button
                  class="px-4 py-2 bg-light-purple hover:bg-purple-700 rounded-md text-sm font-medium text-white disabled:opacity-60"
                  :disabled="isCreatingTicket"
                  @click="submitTicket"
                >
                  {{ isCreatingTicket ? "Creating..." : "Create Ticket" }}
                </button>
              </div>
            </div>
          </div>
        </PopUp>
      </TransWrap>

      <!-- Attachment Preview Modal -->
      <div
        v-if="previewAttachment"
        class="fixed inset-0 z-[120] bg-black/60 grid place-content-center p-4"
        @click="closeAttachmentPreview"
      >
        <div
          class="bg-white dark:bg-dark-blue rounded-lg p-4 max-w-[90vw] max-h-[90vh] overflow-auto"
          @click.stop
        >
          <div class="flex justify-between items-center mb-3">
            <p class="text-sm font-semibold dark:text-white truncate pr-4">
              {{ previewAttachment.data.name }}
            </p>
            <button
              class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              @click="closeAttachmentPreview"
            >
              <Svgs name="close" class="w-5" />
            </button>
          </div>

          <img
            v-if="previewAttachment.type === 'screenshot'"
            :src="previewAttachment.preview"
            alt="Attachment preview"
            class="max-w-[80vw] max-h-[75vh] object-contain"
          />

          <video
            v-else-if="previewAttachment.type === 'video'"
            :src="previewAttachment.preview"
            controls
            playsinline
            class="max-w-[80vw] max-h-[75vh] object-contain"
          />

          <img
            v-else-if="previewAttachment.data.type.startsWith('image/')"
            :src="previewAttachment.preview"
            alt="Attachment preview"
            class="max-w-[80vw] max-h-[75vh] object-contain"
          />

          <video
            v-else-if="previewAttachment.data.type.startsWith('video/')"
            :src="previewAttachment.preview"
            controls
            playsinline
            class="max-w-[80vw] max-h-[75vh] object-contain"
          />
        </div>
      </div>
    </div>
  </div>
  <!--
      An attachment at full size.

      Over the widget rather than in a new tab: a tab loses the conversation
      somebody was reading, and on a phone it loses the app they were in.
    -->
  <div
    v-if="previewFile"
    class="fixed inset-0 z-[130] grid place-content-center bg-black/80 p-4"
    @click="previewFile = null"
  >
    <div class="max-h-[85vh] w-full max-w-3xl" @click.stop>
      <div class="mb-2 flex items-center justify-between gap-3">
        <p class="min-w-0 truncate text-sm font-semibold text-white">
          {{ previewFile.name }}
        </p>
        <div class="flex shrink-0 items-center gap-1">
          <a
            :href="previewFile.url"
            download
            target="_blank"
            rel="noopener"
            class="rounded-lg p-2 text-white/80 hover:bg-white/10 hover:text-white"
            title="Download"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M12 16 6 10l1.4-1.4 3.6 3.6V4h2v8.2l3.6-3.6L18 10l-6 6Zm-7 4v-2h14v2H5Z"
              />
            </svg>
          </a>
          <button
            class="rounded-lg p-2 text-white/80 hover:bg-white/10 hover:text-white"
            @click="previewFile = null"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12 19 6.4Z"
              />
            </svg>
          </button>
        </div>
      </div>

      <div class="overflow-hidden rounded-xl bg-black">
        <img
          v-if="previewKind === 'image'"
          :src="previewFile.url"
          :alt="previewFile.name"
          class="max-h-[75vh] w-full object-contain"
        />
        <video
          v-else-if="previewKind === 'video'"
          :src="previewFile.url"
          controls
          autoplay
          playsinline
          class="max-h-[75vh] w-full"
        />
        <div v-else-if="previewKind === 'audio'" class="bg-white p-6">
          <audio :src="previewFile.url" controls class="w-full" />
        </div>
        <iframe
          v-else-if="previewKind === 'pdf'"
          :src="previewFile.url"
          class="h-[75vh] w-full bg-white"
          title="Preview"
        />
        <!-- Anything a browser cannot draw. Saying so beats an empty frame. -->
        <div v-else class="grid place-content-center bg-white p-10 text-center">
          <p class="text-sm text-gray-500">This one cannot be shown here.</p>
          <a
            :href="previewFile.url"
            download
            target="_blank"
            rel="noopener"
            class="mt-2 text-xs font-bold text-light-purple"
            >Download it</a
          >
        </div>
      </div>
    </div>
  </div>
</template>
